import React, { useState, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Stack,
  useTheme,
  IconButton,
  Avatar,
  alpha,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Visibility as ViewsIcon,
  Share as ShareIcon,
  ThumbUp as ThumbUpIcon,
  Restaurant as RestaurantIcon,
  Timer as TimerIcon,
  Edit as EditIcon,
  LocalFireDepartment as TrendingIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import Veg from "../../Assets/FoodType/veg.png";
import NonVeg from "../../Assets/FoodType/non-veg.png";
import { getRecipeImage } from "../../constants";
import TimeDisplay from "../Common/TimeDisplay";
import { useMutation, gql } from "@apollo/client";
import DeleteConfirmationDialog from "../Dialogs/DeleteConfirmationDialog";

export const GET_MY_RECIPES = gql`
  query GetMyRecipes {
    allMyRecipes {
      id
      recipe_name
      recipe_type
      recipe_category
      recipe_cooking_duration
      serving
      cook_time
      prep_time
      recipe_image
      recipe_tasks
      created
      modified
    }
  }
`;

const DELETE_RECIPE = gql`
  mutation DeleteRecipe($id: ID!) {
    deleteRecipe(id: $id) {
      success
      message
      id
    }
  }
`;

const formatTime = (minutes) => {
  if (!minutes) return "0m";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

const RecipeCard = ({ recipe, onDelete, refetch }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [elevated, setElevated] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRecipe, { loading: deleteLoading }] = useMutation(
    DELETE_RECIPE,
    {
      optimisticResponse: {
        deleteRecipe: {
          __typename: "DeleteRecipeResponse",
          success: true,
          message: "Recipe deleted successfully",
          id: recipe.id,
        },
      },
      update: (cache, { data }) => {
        if (data?.deleteRecipe?.success) {
          // Remove from cache
          cache.evict({ id: `Recipe:${recipe.id}` });
          cache.gc();

          // Update the recipes list in cache
          const { allMyRecipes } = cache.readQuery({
            query: GET_MY_RECIPES,
          }) || { allMyRecipes: [] };
          cache.writeQuery({
            query: GET_MY_RECIPES,
            data: {
              allMyRecipes: allMyRecipes.filter((r) => r.id !== recipe.id),
            },
          });
        }
      },
      onCompleted: async (data) => {
        if (data?.deleteRecipe?.success) {
          setNotification({
            open: true,
            message: "Recipe deleted successfully",
            severity: "success",
          });
          onDelete?.(recipe.id);
          setDeleteOpen(false);

          // Refetch to ensure complete synchronization
          await refetch();
        }
      },
      onError: (error) => {
        setNotification({
          open: true,
          message: error.message || "Failed to delete recipe",
          severity: "error",
        });
      },
    }
  );
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    id,
    recipe_name,
    viewsCount,
    likedCount,
    cookedCount,
    created_by,
    recipe_category,
    recipe_type,
    recipe_status,
    recipe_visiblity,
    recipe_image,
    cook_time,
    prep_time,
    profile_pic,
  } = recipe;

  const isPopular = viewsCount > 1000 || likedCount > 100;
  const totalTimeInMinutes =
    (parseInt(recipe.cook_time) || 0) + (parseInt(recipe.prep_time) || 0);

  const handleDelete = async () => {
    try {
      const { data } = await deleteRecipe({
        variables: { id: recipe.id },
        update: (cache) => {
          // Remove the deleted recipe from Apollo cache
          cache.evict({ id: `Recipe:${recipe.id}` });
          cache.gc();
        },
      });

      if (data?.deleteRecipe?.success) {
        setNotification({
          open: true,
          message: "Recipe deleted successfully",
          severity: "success",
        });
        onDelete?.(recipe.id);
        setDeleteOpen(false);

        // Refetch to ensure data consistency
        await refetch();
      }
    } catch (error) {
      console.error("Error deleting recipe:", error);
      setNotification({
        open: true,
        message: error.message || "Failed to delete recipe",
        severity: "error",
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            onMouseEnter={() => setElevated(true)}
            onMouseLeave={() => setElevated(false)}
            sx={{
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: elevated ? "translateY(-8px)" : "none",
              boxShadow: elevated
                ? `0 12px 24px ${alpha(theme.palette.common.black, 0.15)}`
                : theme.shadows[2],
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Image Section with Overlay */}
            <Box sx={{ position: "relative", paddingTop: "56.25%" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${getRecipeImage(recipe_image)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.7))",
                  },
                }}
              />

              {/* Status & Popular Badges */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  zIndex: 2,
                }}
              >
                {recipe_status && recipe_visiblity !== "Private" && (
                  <Chip
                    label={recipe_status === "Publish" ? "Published" : "Draft"}
                    size="small"
                    color={recipe_status === "Publish" ? "success" : "warning"}
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {isPopular && (
                  <Chip
                    icon={<TrendingIcon sx={{ color: "#fff !important" }} />}
                    label="Popular"
                    size="small"
                    sx={{
                      fontWeight: 600,
                      background: "linear-gradient(45deg, #ff6b6b, #ff8e8e)",
                      color: "white",
                    }}
                  />
                )}
              </Stack>

              {/* Recipe Type Indicators */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  position: "absolute",
                  bottom: 12,
                  right: 12,
                  zIndex: 2,
                }}
              >
                {recipe_type.split(",").map((type) => (
                  <Box
                    key={type.trim()}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "50%",
                      p: 0.5,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    }}
                  >
                    <img
                      src={type.trim() === "Veg" ? Veg : NonVeg}
                      alt={type.trim()}
                      width={20}
                      height={20}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>

            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              {/* Title and Author Section */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Avatar
                  src={profile_pic}
                  alt={created_by}
                  sx={{
                    width: 36,
                    height: 36,
                    border: `2px solid ${theme.palette.primary.main}`,
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      lineHeight: 1.2,
                      display: "-webkit-box",
                      overflow: "hidden",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {recipe_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    by {created_by}
                  </Typography>
                </Box>
              </Stack>

              {/* Categories */}
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                sx={{ mb: 2 }}
                useFlexGap
                gap={0.5}
              >
                {recipe_category
                  .split(",")
                  .slice(0, 2)
                  .map((category) => (
                    <Chip
                      key={category}
                      label={category.trim()}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                      }}
                    />
                  ))}
                {recipe_category.split(",").length > 2 && (
                  <Chip
                    label={`+${recipe_category.split(",").length - 2}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.grey[500], 0.1),
                      color: theme.palette.text.secondary,
                    }}
                  />
                )}
              </Stack>

              {/* Stats and Time */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Views">
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <ViewsIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {viewsCount.toLocaleString()}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Likes">
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <ThumbUpIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {likedCount.toLocaleString()}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title="Times Cooked">
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <RestaurantIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {cookedCount.toLocaleString()}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Stack>
                <TimeDisplay
                  minutes={totalTimeInMinutes}
                  variant="compact"
                  tooltip={`Cook: ${formatTime(
                    recipe.cook_time
                  )} • Prep: ${formatTime(recipe.prep_time)}`}
                />
              </Stack>

              {/* Action Buttons */}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(`/recipe/${id}`)}
                  sx={{
                    py: 1,
                    fontWeight: 600,
                    textTransform: "none",
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  }}
                >
                  View Recipe
                </Button>
                <IconButton
                  onClick={() => navigate(`/edit-recipe/${id}`)}
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.secondary.main, 0.2),
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => setDeleteOpen(true)}
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.error.main, 0.2),
                    },
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      <DeleteConfirmationDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title={`Delete "${recipe_name}"`}
        recipeName={recipe_name}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(RecipeCard);
